// email-core — pure send-side helpers shared by embedded mode (`ctx.email`/the
// built-in send-email) and the standalone mail API (Function URL). Kept free of
// AWS imports so it's testable and extraction-ready into its own package.
//
// Suppression: addresses that bounced/complained are recorded at
// `.s3p/email/suppression/{sha256(addr)}.json`; senders must not email them
// again (deliverability hygiene + the legal must for complaints). The CHECK
// lives here so every send path honors it.

import { emailDomain } from "./conventions.ts";
import { paths } from "./paths.ts";

export const SUPPRESSION_PREFIX = paths().emailSuppression("").replace(/\.json$/, "");

/** Canonical form of an address for matching: the bare `local@domain`,
 *  lower-cased (handles `Name <a@b.com>`). Returns null if unparseable. */
export function normalizeAddress(address: string): string | null {
	const angle = address.match(/<([^>]+)>\s*$/);
	const addr = (angle?.[1] ?? address).trim().toLowerCase();
	const dom = emailDomain(addr);
	if (!dom) return null;
	const at = addr.lastIndexOf("@");
	return `${addr.slice(0, at)}@${dom}`;
}

async function sha256Hex(s: string): Promise<string> {
	const digest = await globalThis.crypto.subtle.digest("SHA-256", new TextEncoder().encode(s));
	return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/** Storage path for a suppressed address (null if the address is malformed). */
export async function suppressionPath(address: string): Promise<string | null> {
	const norm = normalizeAddress(address);
	if (!norm) return null;
	return paths().emailSuppression(await sha256Hex(norm));
}

/** Partition recipients into allowed vs suppressed, given an existence check
 *  (the caller wires it to S3 — e.g. `ctx.db` HEAD/GET on the path). */
export async function filterSuppressed(
	recipients: string[],
	isSuppressed: (path: string) => Promise<boolean>,
): Promise<{ allowed: string[]; suppressed: string[] }> {
	const allowed: string[] = [];
	const suppressed: string[] = [];
	for (const r of recipients) {
		const path = await suppressionPath(r);
		if (path && (await isSuppressed(path))) suppressed.push(r);
		else allowed.push(r);
	}
	return { allowed, suppressed };
}
